import axios from "axios";
import { normalizeAppointmentDates } from "./utils/localDateTime";

const TOKEN_STORAGE_KEY = "ardis-pbi-auth-token";

export const getAuthToken = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get("token");

  if (urlToken) {
    // URL token takes priority over any stored token
    localStorage.setItem(TOKEN_STORAGE_KEY, urlToken);

    // Remove token from URL
    urlParams.delete("token");
    const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : "") + window.location.hash;
    window.history.replaceState({}, document.title, newUrl);
    return urlToken;
  }

  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) {
    // If no token anywhere, redirect to login page
    const redirectUrl = `${window.location.origin}/login-required.html`;
    window.location.href = redirectUrl;
  }

  return token;
};

export const getCustomerId = () => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.customer_id;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL
});
api.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      console.log('Authentication error, redirecting to token page');
      logoutUser();
    }
    return Promise.reject(error);
  }
);

export const logoutUser = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  window.location.href = `${window.location.origin}/login-required.html`;
};

const fetchApiData = async (endpoint, params = {}) => {
  const response = await api.get(`/${endpoint}`, { params });
  return response.data;
};

const updateHourlyRate = async (endpoint, idField, id, hourlyRate) => {
  try {
    const response = await api.patch(`/${endpoint}`, { hourly_rate: hourlyRate });
    return response.data;
  } catch (error) {
    console.error(`Error updating ${endpoint}:`, error.response ? error.response.data : error.message);
    throw error;
  }
};

// Daily endpoints
export const fetchDailyPlannings = (date) => fetchApiData("daily/planning-counts", { date });
export const fetchDailyThroughput = (date, workcell_id = null) => fetchApiData("daily/throughput", { date, workcell_id });
export const fetchDailyThroughputDetails = (date) => fetchApiData("daily/throughput-details", { date });
export const fetchDailyHoursWorked = (date) => fetchApiData("daily/hours-worked", { date });
export const fetchDailyHoursSummary = (date) => fetchApiData("daily/hours-summary", { date });
export const fetchDailyTurnover = (date) => fetchApiData("daily/turnover", { date });

// Monthly endpoints
export const fetchMonthlyPlanningCounts = (date) => fetchApiData("monthly/planning-counts", { start_date: date });
export const fetchMonthlyThroughput = (start_date, workcell_id) => fetchApiData("monthly/throughput", { start_date, workcell_id });
export const fetchMonthlyThroughputDetails = (start_date) => fetchApiData("monthly/throughput-details", { start_date });
export const fetchMonthlyWorkcellActivity = (start_date) => fetchApiData("monthly/workcell-activity", { start_date });
export const fetchMonthlyHoursWorked = (date) => fetchApiData("monthly/hours-worked", { start_date: date });
export const fetchMonthlyTurnover = (date) => fetchApiData("monthly/turnover", { start_date: date });
export const fetchMonthlyThroughputPerHour = (date) => fetchApiData("monthly/throughput-per-hour", { start_date: date });
export const fetchMonthlyOeeSchedule = (date) => fetchApiData("monthly/oee-schedule", { start_date: date });

// Project endpoints
export const fetchPivotProjectProductWorkcell = (project_id) => fetchApiData(`projects/${project_id}/pivot/product-workcell`);
export const fetchPivotProjectProductWorkcellCost = (project_id) => fetchApiData(`projects/${project_id}/pivot/product-workcell-cost`);
export const fetchProjectName = (project_id) => fetchApiData(`projects/${project_id}/name`);
export const fetchAllProjects = () => fetchApiData("projects");

// Resource endpoints
export const fetchAllWorkcells = () => fetchApiData("workcells");
export const fetchAllOperators = () => fetchApiData("operators");

// Activity endpoints
export const fetchOperatorActivity = (date, start_date, end_date) => {
  const params = {};
  if (date) params.date = date;
  if (start_date) params.start_date = start_date;
  if (end_date) params.end_date = end_date;
  return fetchApiData("operators/activity", params);
};

export const fetchWorkcellActivity = (date, start_date, end_date) => {
  const params = {};
  if (date) params.date = date;
  if (start_date) params.start_date = start_date;
  if (end_date) params.end_date = end_date;
  return fetchApiData("workcells/activity", params);
};

// Rate update endpoints
export const updateOperatorHourlyRate = async (id, hourly_rate) => {
  return updateHourlyRate(`operators/${id}/hourly-rate`, "operator_id", id, hourly_rate);
};

export const updateWorkcellHourlyRate = async (id, hourly_rate) => {
  return updateWorkcell(id, { hourly_rate });
};

export const updateWorkcell = async (id, data) => {
  try {
    const response = await api.patch(`/workcells/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating workcell:`, error.response ? error.response.data : error.message);
    throw error;
  }
};

// Upload endpoints
export const uploadFile = async (uploadType, file) => {
  try {
    const fileContent = await file.text();
    const jsonData = JSON.parse(fileContent);
    const response = await api.post(`/upload/${uploadType}`, jsonData);
    return response.data;
  } catch (error) {
    console.error(`Failed to upload ${uploadType} data:`, error);
    throw error;
  }
};

export const fetchAllAppointments = (workcellId) => {
  if (workcellId) {
    return api.get("/appointments", { params: { workcell_id: workcellId } }).then(r => r.data);
  }
  return api.get("/appointments").then(r => r.data);
};

export const createAppointment = (data) => api.post("/appointments", normalizeAppointmentDates(data)).then(r => r.data);

export const updateAppointment = (id, data) => api.put(`/appointments/${id}`, normalizeAppointmentDates(data)).then(r => r.data);

export const deleteAppointment = (id) => api.delete(`/appointments/${id}`).then(r => r.data);

// Planned vs Done endpoint (calendar month containing the given date)
export const fetchWorkcellMonthlyPlannedVsDone = (date, workcellId, tz) =>
  fetchApiData(`workcell/${workcellId}/monthly/${date}/planned-vs-done`, tz ? { tz } : undefined);

export const fetchWorkcellDailyPlannedVsDone = (date, workcellId) =>
  fetchApiData(`workcell/${workcellId}/daily/${date}/planned-vs-done`);

export const fetchWorkcellDailyPlannedVsDoneDetails = (date, workcellId, hour) =>
  fetchApiData(`workcell/${workcellId}/daily/${date}/planned-vs-done/${hour}`);

// Fetch workcell activity for a date range
export const fetchWorkcellActivityRange = (start_date, end_date) =>
  fetchWorkcellActivity(null, start_date, end_date);

// Fetch OEE for a date range (assumes endpoint supports it, otherwise fallback needed)
export const fetchOeeScheduleRange = (start_date, end_date) =>
  fetchApiData("oee-schedule", { start_date, end_date });

// Fetch throughput for a date range and workcell
export const fetchThroughputRange = (start_date, end_date, workcell_id) =>
  fetchApiData("throughput", { start_date, end_date, workcell_id });

// Fetch turnover for a date range
export const fetchTurnoverRange = (start_date, end_date) =>
  fetchApiData("turnover", { start_date, end_date });

// Fetch hours worked for a date range
export const fetchHoursWorkedRange = (start_date, end_date) =>
  fetchApiData("hours-worked", { start_date, end_date });

// Factory floor positions (default)
export async function fetchFactoryFloorPositions() {
  // Auth token is set by axios interceptor
  return api.get("/factory-floor").then(r => r.data);
}

export async function saveFactoryFloorPositions(positions) {
  // Auth token is set by axios interceptor
  return api.post("/factory-floor", positions).then(r => r.data);
}

// OEE per day for a workcell for the calendar month containing the given date
export const fetchWorkcellMonthlyOee = (workcellId, date, tz) =>
  fetchApiData(`workcell/${workcellId}/monthly/${date}/oee`, tz ? { tz } : undefined);

// Workcell stats grid endpoint
export const fetchWorkcellStatsGrid = (start_date, range_days = 28) =>
  fetchApiData("workcell-stats-grid", { start_date, range_days });

export const fetchErrors = (source = 'processing', tail = 200) =>
  fetchApiData('errors', { source, tail });

// Output files listing & download
export const fetchDataList = (path = '') => fetchApiData('data/list', { path });

// Customers
// Fetch a list of customers. Backend should provide an array of customer objects
// with fields like { id, name, token, domain } where token can be used to
// construct a login URL (e.g. https://domain.com/?token=...).
export const fetchCustomers = () => fetchApiData('customers');
export const fetchCustomerSettings = () => fetchApiData("customer-settings");
export const updateCustomerSettings = async (data) => {
  const response = await api.patch("/customer-settings", data);
  return response.data;
};

export const fetchTimeslots = (date, workcellId = null) => {
  const params = { date };
  if (workcellId) params.workcell_id = workcellId;
  return fetchApiData("timeslots", params);
};

export const searchTimeslots = (timeslotId, limit = null) => {
  const params = { timeslot_id: timeslotId };
  if (limit) params.limit = limit;
  return fetchApiData("timeslots/search", params);
};

export const fetchTimeslotImpact = (timeslotId) => (
  fetchApiData(`timeslots/${timeslotId}/impact`)
);

export const deleteTimeslot = (timeslotId, reason = null) => {
  const config = {
    ...(reason ? { data: { reason } } : {})
  };
  return api.delete(`/timeslots/${timeslotId}`, config).then(r => r.data);
};

// Download helpers that always return a Blob and filename.
function extractFilenameFromContentDisposition(cd) {
  if (!cd) return null;
  const m = cd.match(/filename\*=UTF-8''([^;\n\r]+)/i);
  if (m && m[1]) return decodeURIComponent(m[1]);
  const m2 = cd.match(/filename="?([^";]+)"?/i);
  if (m2 && m2[1]) return m2[1];
  return null;
}

async function downloadBlobFromEndpoint(url, params, fallbackFilename) {
  const resp = await api.get(url, { params, responseType: 'blob' });
  const filename = extractFilenameFromContentDisposition(resp.headers['content-disposition']) || fallbackFilename;
  return { blob: resp.data, filename };
}

export const downloadDataFile = async (path) => {
  const resp = await api.get('/data/download', { params: { path }, responseType: 'blob' });
  const contentType = resp.headers['content-type'] || '';
  if (contentType.includes('application/json')) {
    // The endpoint sometimes returns a JSON metadata object { url: "..." }
    // (for example when using presigned S3 URLs). Other times it returns the
    // actual JSON file contents (content-type: application/json). If we
    // receive metadata with a `url`, fetch that URL. If not, treat the
    // response blob as the actual file contents and return it.
    const text = await resp.data.text();
    let json = null;
    try {
      json = JSON.parse(text || '{}');
    } catch (e) {
      json = null; // not parseable JSON metadata, treat as file contents
    }

    if (json && json.url) {
      const r = await fetch(json.url);
      const blob = await r.blob();
      const cd = r.headers.get('content-disposition');
      const filename = extractFilenameFromContentDisposition(cd) || path.split('/').pop();
      return { blob, filename };
    }

    // No url found: resp.data contains the actual JSON file contents.
    // Reuse the blob we already received. Since we've consumed it to get
    // text, recreate a Blob from the text so callers get a proper Blob.
    const blob = new Blob([text], { type: 'application/json' });
    const filename = extractFilenameFromContentDisposition(resp.headers['content-disposition']) || path.split('/').pop();
    return { blob, filename };
  }
  const filename = extractFilenameFromContentDisposition(resp.headers['content-disposition']) || path.split('/').pop();
  return { blob: resp.data, filename };
};

export const downloadDataArchive = async (path) => {
  const folderName = path.split('/').filter(Boolean).pop() || 'data';
  return downloadBlobFromEndpoint(
    '/data/download-archive',
    { path },
    `${folderName}.zip`
  );
};
